import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

/** Seconds per loop at one copy, used when `data-speed-factor` is missing or not numeric. */
const DEFAULT_SPEED_FACTOR = 50;

/**
 * A custom element that displays a marquee.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} wrapper - The wrapper element.
 * @property {HTMLElement} content - The content element.
 * @property {HTMLElement[]} marqueeItems - The marquee items collection.
 * @property {HTMLButtonElement} [pauseButton] - The pause button, when the section shows one.
 *
 * @extends Component<Refs>
 */
class MarqueeComponent extends Component {
  requiredRefs = ['wrapper', 'content', 'marqueeItems'];

  async connectedCallback() {
    super.connectedCallback();

    const { marqueeItems } = this.refs;
    if (marqueeItems.length === 0) return;

    const { numberOfCopies } = await this.#queryNumberOfCopies();

    const speed = this.#calculateSpeed(numberOfCopies);

    this.#addRepeatedItems(numberOfCopies - 1);
    this.#duplicateContent();

    this.#setSpeed(speed);

    window.addEventListener('resize', this.#handleResize);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#handleResize);
  }

  /** The pause button's action: assets/marquee.css stops the loop on `data-paused`. */
  togglePause() {
    const paused = this.toggleAttribute('data-paused');

    this.refs.pauseButton?.setAttribute('aria-pressed', String(paused));
  }

  /**
   * @type {number | null}
   */
  #marqueeWidth = null;

  get clonedContent() {
    const { content, wrapper } = this.refs;
    const lastChild = wrapper.lastElementChild;

    return content !== lastChild ? lastChild : null;
  }

  /**
   * Writes the animation duration. A non-finite value makes the `animation` shorthand invalid at
   * computed-value time, which drops the animation entirely and freezes the marquee until reload —
   * so keep the last good value instead of writing garbage.
   *
   * @param {number} value
   */
  #setSpeed(value) {
    if (!Number.isFinite(value) || value <= 0) return;

    this.style.setProperty('--marquee-speed', `${value}s`);
  }

  async #queryNumberOfCopies() {
    const { marqueeItems } = this.refs;

    return new Promise((resolve) => {
      if (!marqueeItems[0]) {
        // Wrapping the resolve in a setTimeout here and below splits each marquee reflow into a separate task.
        return setTimeout(() => resolve({ numberOfCopies: 1, isHorizontalResize: true }), 0);
      }

      const intersectionObserver = new IntersectionObserver(
        (entries) => {
          const firstEntry = entries[0];
          if (!firstEntry) return;
          intersectionObserver.disconnect();

          const { width: marqueeWidth } = firstEntry.rootBounds ?? { width: 0 };
          const { width: marqueeItemsWidth } = firstEntry.boundingClientRect;

          const isHorizontalResize = this.#marqueeWidth !== marqueeWidth;
          this.#marqueeWidth = marqueeWidth;

          setTimeout(() => {
            resolve({
              numberOfCopies: marqueeItemsWidth === 0 ? 1 : Math.ceil(marqueeWidth / marqueeItemsWidth),
              isHorizontalResize,
            });
          }, 0);
        },
        { root: this }
      );
      intersectionObserver.observe(marqueeItems[0]);
    });
  }

  /**
   * @param {number} numberOfCopies
   */
  #calculateSpeed(numberOfCopies) {
    const speedFactor = Number(this.getAttribute('data-speed-factor')) || DEFAULT_SPEED_FACTOR;
    const copies = Number.isFinite(numberOfCopies) && numberOfCopies > 0 ? numberOfCopies : 1;

    return Math.sqrt(copies) * speedFactor;
  }

  #handleResize = debounce(async () => {
    // `#queryNumberOfCopies` resolves `numberOfCopies` — destructuring any other name yields
    // undefined, which propagates to `--marquee-speed: NaNs` and kills the animation.
    const { numberOfCopies: newNumberOfCopies, isHorizontalResize } = await this.#queryNumberOfCopies();

    // opt out of marquee manipulation on vertical resizes
    if (!isHorizontalResize) return;

    // Count the live rows rather than `refs.marqueeItems`: refs are live, so the aria-hidden
    // duplicate's rows are in there too and the array reports twice the real number of copies.
    const currentNumberOfCopies = this.refs.content.children.length;
    const speed = this.#calculateSpeed(newNumberOfCopies);
    const progress = this.#animationProgress();

    if (newNumberOfCopies > currentNumberOfCopies) {
      this.#addRepeatedItems(newNumberOfCopies - currentNumberOfCopies);
    } else if (newNumberOfCopies < currentNumberOfCopies) {
      this.#removeRepeatedItems(currentNumberOfCopies - newNumberOfCopies);
    }

    this.#duplicateContent();
    this.#setSpeed(speed);
    this.#resumeAnimation(progress);
  }, 250);

  /**
   * Where the loop currently sits, as a 0-1 fraction of one iteration.
   *
   * @returns {number}
   */
  #animationProgress() {
    const [animation] = this.refs.wrapper.getAnimations();

    return animation?.effect?.getComputedTiming().progress ?? 0;
  }

  /**
   * Puts the loop back where it was after a resize. The copy count changes the duration while
   * `currentTime` stays absolute, so both keeping it and zeroing it make the marquee jump.
   * Rewriting it as `progress x new duration` keeps the movement continuous at any viewport width.
   *
   * @param {number} progress - Iteration progress captured before the rows changed.
   */
  #resumeAnimation(progress) {
    requestAnimationFrame(() => {
      for (const animation of this.refs.wrapper.getAnimations()) {
        const duration = animation.effect?.getComputedTiming().duration;

        if (typeof duration !== 'number' || !Number.isFinite(duration)) continue;

        animation.currentTime = progress * duration;
      }
    });
  }

  #duplicateContent() {
    this.clonedContent?.remove();

    const clone = /** @type {HTMLElement} */ (this.refs.content.cloneNode(true));

    clone.setAttribute('aria-hidden', 'true');
    clone.removeAttribute('ref');

    // The duplicate is pure decoration. Leaving `ref` on its descendants would register every
    // cloned row in `refs.marqueeItems` and corrupt the copy count the resize handler reads.
    for (const element of clone.querySelectorAll('[ref]')) {
      element.removeAttribute('ref');
    }

    this.refs.wrapper.appendChild(clone);
  }

  /**
   * Appends extra rows. Takes the number of rows to ADD, not the target total — the resize handler
   * passes a delta, so subtracting one in here would always add one row too few.
   *
   * @param {number} count
   */
  #addRepeatedItems(count) {
    const { content, marqueeItems } = this.refs;

    if (!marqueeItems[0]) return;

    for (let i = 0; i < count; i++) {
      content.appendChild(marqueeItems[0].cloneNode(true));
    }
  }

  /**
   * @param {number} numberOfCopies
   */
  #removeRepeatedItems(numberOfCopies) {
    const { content } = this.refs;
    const children = Array.from(content.children);

    const itemsToRemove = Math.min(numberOfCopies, children.length - 1);

    for (let i = 0; i < itemsToRemove; i++) {
      content.lastElementChild?.remove();
    }
  }
}

if (!customElements.get('marquee-component')) {
  customElements.define('marquee-component', MarqueeComponent);
}
